import { useEffect, useState } from "react";
import { Tab, Tabs } from "@mui/material";
import { ConfirmModal } from "@/components/ConfirmModal";
import { CrudPage } from "@/components/crud/CrudPage";
import { useAuth } from "@/context/AuthContext";
import { UserRoles } from "@/api/user.service";
import { getModuleRoles } from "@/api/moduleRole.service";
import { obterFluxo } from "@/api/metas.service";
import { MetasFeriadosTab } from "./MetasFeriadosTab";
import { MetasDistribuicaoAreaTab } from "./MetasDistribuicaoAreaTab";
import { MetasContribuicaoTab } from "./MetasContribuicaoTab";
import { MetasPeriodosTab } from "./MetasPeriodosTab";
import { MetasManutencaoTab } from "./MetasManutencaoTab";
import { MetasResponsaveisTab } from "./MetasResponsaveisTab";
import { MetasAprovacoesTab } from "./MetasAprovacoesTab";

// Metas' other screens (goal calculation, approval, dashboard, ...) land
// here as further tabs; "feriados", "contribuicao", "distribuicao", "periodos", "manutencao",
// "aprovacoes" and "responsaveis" are the ones built so far.
type MetasTab = "feriados" | "contribuicao" | "distribuicao" | "periodos" | "manutencao" | "aprovacoes" | "responsaveis";

// Mesmo valor que o produto confere (creatorRole em metas/cenarios.go).
const PAPEL_CRIADOR = "Criador de Meta";

export function MetasPage() {
  const { user } = useAuth();
  // Operador com código ERP (o antigo comprador) só opera na manutenção — o
  // back-end bloqueia os methods das outras abas, então nem as oferecemos.
  const restritoPeloCodigoErp = user?.role === UserRoles.Operador && !!user.codigo_erp?.trim();
  const [ehCriador, setEhCriador] = useState(false);
  const clientId = user?.loggedClient?.id;
  const userId = user?.id;
  const isCliente = user?.role === UserRoles.Cliente;
  useEffect(() => {
    if (!userId || !clientId) {
      setEhCriador(false);
      return;
    }
    let cancelado = false;
    getModuleRoles(userId, clientId, "metas")
      .then((r) => !cancelado && setEhCriador((r.roles ?? []).includes(PAPEL_CRIADOR)))
      .catch(() => !cancelado && setEhCriador(false));
    return () => {
      cancelado = true;
    };
  }, [userId, clientId]);
  // A aba Aprovações só existe com o fluxo ligado; quem o liga e desliga é a aba Permissões.
  const [fluxoHabilitado, setFluxoHabilitado] = useState(false);
  useEffect(() => {
    if (!clientId) return;
    let cancelado = false;
    obterFluxo(clientId)
      .then((f) => !cancelado && setFluxoHabilitado(f.habilitado))
      .catch(() => !cancelado && setFluxoHabilitado(false));
    return () => {
      cancelado = true;
    };
  }, [clientId]);
  // O cadastro de responsáveis é de quem é "Criador de Meta" e usuário do tipo cliente.
  const podeConfigurar = isCliente && ehCriador && !restritoPeloCodigoErp;
  // Criar, alterar e excluir cenários exigem o papel, e sem fluxo de aprovação
  // ajustar e publicar também — como no produto (checkCreatorRole: admin da
  // plataforma passa direto).
  const temPapelCriador = user?.role === UserRoles.Admin || ehCriador;
  const [tab, setTab] = useState<MetasTab>(restritoPeloCodigoErp ? "manutencao" : "feriados");
  // A Manutenção informa se há ajustes não salvos: trocar de aba desmonta a tela e os perderia.
  const [ajustesPendentes, setAjustesPendentes] = useState(false);
  const [abaDestino, setAbaDestino] = useState<MetasTab | null>(null);

  const trocarAba = (destino: MetasTab) => {
    if (destino === tab) return;
    if (tab === "manutencao" && ajustesPendentes) setAbaDestino(destino);
    else setTab(destino);
  };

  const confirmarTroca = () => {
    if (abaDestino) setTab(abaDestino);
    setAbaDestino(null);
  };

  return (
    <CrudPage title="Metas">
      <Tabs
        value={tab}
        onChange={(_, value: MetasTab) => trocarAba(value)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        {!restritoPeloCodigoErp && <Tab label="Feriados e Sazonalidade" value="feriados" />}
        {!restritoPeloCodigoErp && <Tab label="Contribuição" value="contribuicao" />}
        {!restritoPeloCodigoErp && <Tab label="Distribuição de Área" value="distribuicao" />}
        {!restritoPeloCodigoErp && <Tab label="Períodos de Cálculo" value="periodos" />}
        <Tab label="Manutenção de Metas" value="manutencao" />
        {fluxoHabilitado && !restritoPeloCodigoErp && <Tab label="Aprovações" value="aprovacoes" />}
        {podeConfigurar && <Tab label="Permissões" value="responsaveis" />}
      </Tabs>

      {tab === "feriados" && <MetasFeriadosTab />}
      {tab === "contribuicao" && <MetasContribuicaoTab />}
      {tab === "distribuicao" && <MetasDistribuicaoAreaTab />}
      {tab === "periodos" && <MetasPeriodosTab temPapelCriador={temPapelCriador} />}
      {tab === "manutencao" && <MetasManutencaoTab temPapelCriador={temPapelCriador} onAjustesPendentes={setAjustesPendentes} />}
      {tab === "aprovacoes" && fluxoHabilitado && !restritoPeloCodigoErp && <MetasAprovacoesTab onAbrirManutencao={() => trocarAba("manutencao")} />}
      {tab === "responsaveis" && podeConfigurar && <MetasResponsaveisTab onFluxoChange={setFluxoHabilitado} />}

      <ConfirmModal
        open={!!abaDestino}
        onClose={() => setAbaDestino(null)}
        onConfirm={confirmarTroca}
        title="Ajustes não salvos"
        text="Há ajustes na Manutenção de Metas que ainda não foram salvos e eles serão perdidos ao trocar de aba. Deseja continuar?"
        btnText="Trocar de aba"
      />
    </CrudPage>
  );
}
